import { api } from "../lib";
import type { ClientIdentityDirective } from "../types/clientIdentity";
import type {
  CaseIdentity,
  CasePrefixReassignmentContext,
} from "../types/casePrefixReassignment";
import type { DocumentViewResponse } from "../types/reportTemplate";
import type {
  IssuedReportReview,
  ReportContentVersion,
  ReportPreviewData,
  ReportPreviewEvidence,
} from "./signOff.service";
import { ReportContentChangedError } from "./signOff.service";
import type { FragmentCountQualifier } from "../utils/fragmentCount";

type ClearableString = string | null;

// Request body for both direct update and correction request filing
export interface PatientInfoUpdateBody {
  patient?: {
    firstName?: ClearableString;
    lastName?: ClearableString;
    dateOfBirth?: ClearableString;
    sex?: ClearableString;
    medicalRecordNumber?: ClearableString;
  };
  contact?: {
    mobilePhone?: ClearableString;
    homePhone?: ClearableString;
    workPhone?: ClearableString;
    email?: ClearableString;
  };
  address?: {
    street?: ClearableString;
    state?: ClearableString;
    city?: ClearableString;
    zipCode?: ClearableString;
  };
  primaryInsurance?: {
    policyNumber?: ClearableString;
    authorizationNumber?: ClearableString;
    groupNumber?: ClearableString;
    groupName?: ClearableString;
    patientRelationToInsured?: ClearableString;
    address?: {
      street?: ClearableString;
      state?: ClearableString;
      city?: ClearableString;
      zipCode?: ClearableString;
    };
  };
  secondaryInsurance?: {
    policyNumber?: ClearableString;
    authorizationNumber?: ClearableString;
    groupNumber?: ClearableString;
    groupName?: ClearableString;
    patientRelationToInsured?: ClearableString;
    address?: {
      street?: ClearableString;
      state?: ClearableString;
      city?: ClearableString;
      zipCode?: ClearableString;
    };
  };
  caseInfo?: {
    sendingPracticeId?: string | null;
    physicianId?: string | null;
    surgeryCenterLabId?: string | null;
    submittingLabId?: string | null;
    procedureId?: string;
    clinicalHistory?: string;
    clinicalHistoryCodes?: string[];
    indications?: string;
    impressions?: string;
    externalCaseNumber?: string;
    ruleOuts?: string;
    dateReceived?: string;
    dateCollected?: string;
    originatingPracticeId?: string;
    originatingPhysicianId?: string;
    originatingSurgeryCenterId?: string;
    consultationReason?: string;
    consultationOriginalDiagnosis?: string;
    consultationClinicalContext?: string;
    requestingPathologist?: string;
    shippingPathologist?: string;
    shippingStreet?: string;
    shippingState?: string;
    shippingCity?: string;
    shippingZipCode?: string;
  };
  clientIdentity?: ClientIdentityDirective;
  note?: string;
  caseErrorReportId?: string | null;
}

export interface DirectCorrectionPrefixReassignment {
  targetPrefixId: string;
  expectedCurrentCaseNumber: string;
  expectedCurrentPrefixId: string;
  caseNumberRemainder?: string;
}

export interface DirectCorrectionSpecimenValues {
  sourceId: string | null;
  subsiteId: string | null;
  collectionMethod: string | null;
  findings: string | null;
  material: string | null;
  fragmentCount: number | null;
  fragmentCountQualifier: FragmentCountQualifier | null;
  internalGrossCodeId: string | null;
  grossDescription: string | null;
}

export interface DirectCorrectionSpecimenChange {
  specimenId: string;
  expected: DirectCorrectionSpecimenValues;
  changes: Partial<DirectCorrectionSpecimenValues>;
}

export interface DirectCorrectionAmendmentBody extends PatientInfoUpdateBody {
  reason: string;
  reportTimezoneCorrection?: boolean;
  prefixReassignment?: DirectCorrectionPrefixReassignment;
  specimens?: DirectCorrectionSpecimenChange[];
  correctionId?: string;
  correctionTimestamp?: string;
  previewEvidence?: ReportPreviewEvidence;
}

export interface ReportTimezoneCorrectionContext {
  eligible: boolean;
  alreadyCorrected: boolean;
  timeZone: string;
  affectedReportVersions: Array<{
    signOffId: string;
    reportVersion: number;
    signOffType: string;
    isCurrent: boolean;
  }>;
  changes: Record<string, { label?: string; before: unknown; after: unknown }>;
}

export interface DirectCorrectionPreviewResponse {
  previewData: ReportPreviewData;
  documentViewData: DocumentViewResponse;
  templateId: string | null;
  review: IssuedReportReview;
  correctionId: string;
  correctionTimestamp: string;
}

export interface DirectCorrectionAmendmentResponse {
  success: boolean;
  amendmentId?: string;
  reportVersion?: number;
  identityChange?: {
    previousIdentity: CaseIdentity;
    currentIdentity: CaseIdentity;
    staleMaterialTypes: Array<"cassettes" | "slides">;
  };
}

export interface DirectUpdateResponse {
  success: boolean;
  message: string;
  caseId: string;
  changedFields: Record<string, { before: any; after: any }>;
  auditRecordId: string;
  identityChange?: {
    previousCaseNumber: string;
    newCaseNumber: string;
    staleMaterialTypes: Array<"cassettes" | "slides">;
    pathologistNotified: boolean;
  };
}

export interface CorrectionRequestResponse {
  success: boolean;
  message: string;
  caseId: string;
  correctionRequestId: string;
  status: "pending";
  requestedAt: string;
  proposedChanges: Record<string, { before: any; after: any }>;
  note?: string;
  messageToRequester: string;
}

export interface AuditRecord {
  id: string;
  editedAt: string;
  editor: { id: string; name: string; email: string };
  changes: Record<string, { before: any; after: any }>;
}

export interface PatientInfoHistoryResponse {
  success: boolean;
  caseId: string;
  auditRecords: AuditRecord[];
}

interface PatientInfoApiError {
  code?: string;
  response?: {
    status?: number;
    data?: {
      code?: string;
      fieldErrors?: Record<string, string>;
    };
  };
}

const correctionErrorMessages: Record<string, string> = {
  CASE_PREFIX_REASSIGNMENT_STALE:
    "This case number changed while you were editing. Reopen the correction and try again.",
  CASE_PREFIX_REASSIGNMENT_BLOCKED:
    "This signed case cannot have its prefix changed in its current state.",
  CASE_PREFIX_UNAVAILABLE:
    "That prefix is no longer available. Choose another prefix and try again.",
  CASE_NUMBER_ALREADY_EXISTS:
    "That case number is already in use. Enter a different number and try again.",
  INVALID_CANONICAL_CASE_NUMBER:
    "The case number does not match the selected prefix. Review it and try again.",
  SAME_CASE_PREFIX: "Choose a prefix different from the current case prefix.",
};

const correctionStatusMessages: Record<number, string> = {
  400: "Review the correction fields and try again.",
  403: "You do not have permission to complete signed-case corrections.",
  404: "This case is no longer available. Refresh the page and try again.",
  409: "This case changed while you were editing. Refresh it and try again.",
  422: "Review the correction fields and try again.",
};

function translatePatientInfoError(
  error: unknown,
  fallbackMessage: string
): Error {
  const apiError = error as PatientInfoApiError;
  const status = apiError.response?.status;
  const code = apiError.response?.data?.code ?? apiError.code;
  const message =
    (code ? correctionErrorMessages[code] : undefined) ??
    (status ? correctionStatusMessages[status] : undefined) ??
    fallbackMessage;
  return Object.assign(new Error(message), {
    code,
    response: {
      status,
      data: { code, fieldErrors: apiError.response?.data?.fieldErrors },
    },
  });
}

async function getSignedCorrectionPrefixContext(
  caseId: string
): Promise<CasePrefixReassignmentContext> {
  try {
    return await api.get<CasePrefixReassignmentContext>(
      `/cases/${caseId}/signed-correction-prefix-context`
    );
  } catch (error) {
    throw translatePatientInfoError(
      error,
      "The case prefix options could not be loaded. Close and reopen the correction."
    );
  }
}

async function getReportTimezoneCorrectionEligibility(
  caseId: string
): Promise<ReportTimezoneCorrectionContext> {
  try {
    return await api.get<ReportTimezoneCorrectionContext>(
      `/cases/${caseId}/report-timezone-correction-eligibility`
    );
  } catch (error) {
    throw translatePatientInfoError(
      error,
      "The report timezone correction status could not be loaded. Close and reopen the correction."
    );
  }
}

async function applyDirectCorrectionAmendment(
  caseId: string,
  body: DirectCorrectionAmendmentBody
): Promise<DirectCorrectionAmendmentResponse> {
  try {
    return await api.post<DirectCorrectionAmendmentResponse>(
      `/cases/${caseId}/direct-correction`,
      body
    );
  } catch (error) {
    throw translatePatientInfoError(
      error,
      "The correction could not be applied. Try again."
    );
  }
}

async function getDirectCorrectionPreview(
  caseId: string,
  body: DirectCorrectionAmendmentBody
): Promise<DirectCorrectionPreviewResponse> {
  try {
    return await api.post<DirectCorrectionPreviewResponse>(
      `/cases/${caseId}/direct-correction/preview`,
      body
    );
  } catch (error) {
    throw translatePatientInfoError(
      error,
      "The corrected report preview could not be generated. Try again."
    );
  }
}

async function acknowledgeDirectCorrectionPreview(
  caseId: string,
  body: DirectCorrectionAmendmentBody,
  contentVersion: ReportContentVersion
): Promise<ReportPreviewEvidence> {
  try {
    const response = await api.post<
      ReportPreviewEvidence | { previewEvidence: ReportPreviewEvidence }
    >(`/cases/${caseId}/direct-correction/preview-review`, {
      ...body,
      contentVersion,
    });
    return "previewEvidence" in response ? response.previewEvidence : response;
  } catch (error) {
    const apiError = error as PatientInfoApiError;
    if (
      apiError.response?.status === 409 &&
      apiError.response?.data?.code === "REPORT_CONTENT_CHANGED"
    ) {
      throw new ReportContentChangedError();
    }
    throw translatePatientInfoError(
      error,
      "The corrected report review could not be recorded. Try again."
    );
  }
}

export const patientInfoService = {
  // T016: Direct update OR correction request (same endpoint, backend decides based on case status)
  updatePatientInfo: async (
    caseId: string,
    body: PatientInfoUpdateBody
  ): Promise<DirectUpdateResponse | CorrectionRequestResponse> => {
    return api.post(`/cases/${caseId}/patient-info-update`, body);
  },

  // T016: Get audit history
  getPatientInfoHistory: async (
    caseId: string
  ): Promise<PatientInfoHistoryResponse> => {
    return api.get(`/cases/${caseId}/patient-info-history`);
  },

  // T021: Explicit correction request (alias - same endpoint, used when we know it's post-signoff)
  createCorrectionRequest: async (
    caseId: string,
    body: PatientInfoUpdateBody
  ): Promise<CorrectionRequestResponse> => {
    return api.post(`/cases/${caseId}/patient-info-update`, body);
  },

  getSignedCorrectionPrefixContext,
  getReportTimezoneCorrectionEligibility,

  // Direct correction amendment: applies all field changes and creates a CaseSignOffHistory entry
  applyDirectCorrectionAmendment,
  getDirectCorrectionPreview,
  acknowledgeDirectCorrectionPreview,
};
